import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RouteComponentProps, navigate } from '@reach/router';
import {
  Grid,
  Box,
  Stack,
  Select,
  MenuItem,
  Paper,
  Divider,
  Tabs,
  Tab,
  Chip,
  Switch,
  FormControlLabel,
  IconButton,
  InputAdornment
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LocationDetails from './accountDetails';
import {
  callGetSyncCoreUsers,
  callStartSyncCoreContacts,
  callGetJobStatus,
  callUpdateCoreMeta,
  callGetCoreMeta,
  useCoreDetailedList
} from '@api';
import CoreUsers from './users';
import CoreContacts from './contacts';
import { Alert, Button, Text, TextField } from '@atoms';
import CoreOffices from './offices';
import { resolveBusinessType } from './offices/officeFieldVisibility';
import { toast } from 'react-toastify';
import UploadKnowledgeBaseCSV from './knowledgeBase';
import { DateTime } from 'luxon';

interface CoreDetailsProps extends RouteComponentProps {
  locationId?: string;
}

type CoreDataTabKey = 'offices' | 'users' | 'contacts';

const SERVICE_TYPE_OPTIONS: { value: CoreServiceType; label: string }[] = [
  { value: 'qualify', label: 'Qualify' },
  { value: 'notify', label: 'Notify' },
  { value: 'skip', label: 'Skip' },
  { value: 'out-of-hours', label: 'Out of Hours' }
];

const BUSINESS_DAYS: { key: keyof BusinessHours; label: string }[] = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' }
];

// 30-minute increments from 00:00 to 23:30, value as 24h "HH:mm", label as 12h am/pm.
const TIME_OPTIONS: { value: string; label: string }[] = Array.from({ length: 48 }, (_, i) => {
  const hour = Math.floor(i / 2);
  const minute = i % 2 === 0 ? '00' : '30';
  const value = `${String(hour).padStart(2, '0')}:${minute}`;
  const period = hour < 12 ? 'am' : 'pm';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return { value, label: `${hour12}:${minute} ${period}` };
});

const DEFAULT_BUSINESS_HOURS: BusinessHours = {
  monday: { enabled: true, open: '09:00', close: '17:00' },
  tuesday: { enabled: true, open: '09:00', close: '17:00' },
  wednesday: { enabled: true, open: '09:00', close: '17:00' },
  thursday: { enabled: true, open: '09:00', close: '17:00' },
  friday: { enabled: true, open: '09:00', close: '17:00' },
  saturday: { enabled: false, open: '09:00', close: '17:00' },
  sunday: { enabled: false, open: '09:00', close: '17:00' }
};

const CoreDetails: React.FC<CoreDetailsProps> = (props: CoreDetailsProps) => {
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [syncingContacts, setSyncingContacts] = useState<boolean>(false);
  const [contactsRefreshTrigger, setContactsRefreshTrigger] = useState<number>(0);
  const [coreMeta, setCoreMeta] = useState<CoreMeta>();
  const [coreMetaSave, setCoreMetaSave] = useState<boolean>(false);
  const [knowledgeUpload, setKnowledgeUpload] = useState<boolean>(false);
  const [showZooplaSecret, setShowZooplaSecret] = useState<boolean>(false);

  const coreMetaQuery = useQuery({
    queryKey: ['core-meta', props.locationId],
    queryFn: () => callGetCoreMeta(props.locationId!),
    enabled: !!props.locationId
  });

  // Mirror server payload into local mutable coreMeta. The page applies
  // optimistic edits in onSwitchChange and only writes back on save, so we
  // can't bind directly to the query data.
  useEffect(() => {
    if (coreMetaQuery.data?.code === 200 && coreMetaQuery.data.data) {
      setCoreMeta(coreMetaQuery.data.data.data);
    }
  }, [coreMetaQuery.data]);

  const getTabFromSearch = (search?: string): CoreDataTabKey => {
    const params = new URLSearchParams(search ?? '');
    const tab = params.get('tab') as CoreDataTabKey | null;
    if (tab === 'offices' || tab === 'users' || tab === 'contacts') return tab;
    return 'offices';
  };

  const [activeCoreTab, setActiveCoreTab] = useState<CoreDataTabKey>(() =>
    getTabFromSearch(props.location?.search)
  );

  useEffect(() => {
    // Keep tab in sync with URL (back/forward + shared links)
    setActiveCoreTab(getTabFromSearch(props.location?.search));
  }, [props.location?.search]);

  const setCoreTabInUrl = async (tab: CoreDataTabKey) => {
    const search = props.location?.search ?? '';
    const params = new URLSearchParams(search);
    params.set('tab', tab);
    await navigate(`?${params.toString()}`, { replace: true });
  };

  const { data: coreData, refetch: coreRefetch } = useCoreDetailedList<{ data: CoreLocation[] }>({
    locationId: props.locationId,
    startDate: useMemo(() => {
      // Fixed once per location to avoid changing query key and re-fetching repeatedly
      return DateTime.now()
        .minus({ days: 1 })
        .toISO({ suppressMilliseconds: true, includeOffset: false });
    }, [props.locationId]),
    endDate: useMemo(() => {
      // Fixed once per location to avoid changing query key and re-fetching repeatedly
      return DateTime.now().toISO({ suppressMilliseconds: true, includeOffset: false });
    }, [props.locationId])
  });

  const apiSyncUsers = async () => {
    setActionLoading(true);
    const { data, code } = await callGetSyncCoreUsers(props.locationId as string);
    if (data && code == 200) {
      coreRefetch();
    }
    setActionLoading(false);
  };

  const apiSyncContacts = async () => {
    if (!props.locationId || syncingContacts) return;
    setSyncingContacts(true);
    try {
      const { data, code } = await callStartSyncCoreContacts(props.locationId);
      if (code !== 202 || !data?.jobId) {
        toast.error('Failed to start contact sync');
        return;
      }
      const jobId = data.jobId;
      const poll = async (): Promise<void> => {
        const res = await callGetJobStatus(jobId);
        if (res.code !== 200 || !res.data) return;
        const status = res.data.status;
        if (status === 'completed') {
          toast.success('Contacts synced successfully');
          setContactsRefreshTrigger((t) => t + 1);
          setSyncingContacts(false);
          return;
        }
        if (status === 'failed') {
          toast.error(res.data.errorMessage || 'Contact sync failed');
          setSyncingContacts(false);
          return;
        }
        setTimeout(poll, 2500);
      };
      setTimeout(poll, 2500);
    } catch (e) {
      toast.error('Failed to start contact sync');
      setSyncingContacts(false);
    }
  };

  const onSwitchChange = async (key: string, value: CoreIntegrationType | string | boolean) => {
    setCoreMetaSave(true);
    setCoreMeta((state) => {
      if (!state) return state;
      return {
        ...state,
        [key]: value
      } as CoreMeta;
    });
  };

  const onIntroducersChange = (introducers: MabIntroducer[]) => {
    setCoreMetaSave(true);
    setCoreMeta((state) => {
      if (!state) return state;
      return { ...state, mabIntroducers: introducers } as CoreMeta;
    });
  };

  const onBusinessHoursChange = async (
    day: keyof BusinessHours,
    field: keyof BusinessHoursDay,
    value: boolean | string
  ) => {
    setCoreMetaSave(true);
    setCoreMeta((state) => {
      if (!state) return state;
      const current = state.businessHours ?? DEFAULT_BUSINESS_HOURS;
      return {
        ...state,
        businessHours: {
          ...current,
          [day]: { ...current[day], [field]: value }
        }
      } as CoreMeta;
    });
  };

  const onSaveMeta = async () => {
    if (!coreMeta) return;
    setActionLoading(true);

    // Validate X-API-KEY if RAN is enabled
    if (coreMeta?.supplierNumberEnabled === 'ran') {
      if (!coreMeta?.ranApiKey?.trim())
        return toast.error('X-API-KEY is required when RAN is enabled');
    }

    if (coreMeta?.zooplaProspectPlusEnabled) {
      if (!coreMeta?.zooplaClientId?.trim()) return toast.error('Zoopla Client ID is required');
      if (!coreMeta?.zooplaClientSecret?.trim())
        return toast.error('Zoopla Client Secret is required');
      if (!coreMeta?.zooplaFilterId?.trim()) return toast.error('Zoopla Filter ID is required');
    }

    switch (coreMeta?.integration) {
      case 'reapit':
        if (!coreMeta?.reapitCustomerId?.trim())
          return toast.error('Reapit Customer ID is required');
        break;
      case 'homeflow':
        if (!coreMeta?.homeFlowToken?.trim()) return toast.error('Home Flow Token is required');
        break;
      case 'alto':
        if (!coreMeta?.altoAgentKey?.trim()) return toast.error('Agent Key is required');
        if (!coreMeta?.altoAgentId?.trim()) return toast.error('Agent ID is required');
        if (!coreMeta?.altoAgentUsername?.trim()) return toast.error('Agent Username is required');
        if (!coreMeta?.altoAgentPassword?.trim()) return toast.error('Agent Password is required');
        break;
      case 'leadpro':
        if (!coreMeta?.leadProApiKey?.trim()) return toast.error('LeadPro API Key is required');
        break;
      case 'acquaint':
        if (!coreMeta?.acquaintApiKey?.trim()) return toast.error('Acquaint API Key is required');
        if (!coreMeta?.acquaintSitePreFix?.trim())
          return toast.error('Acquaint Site PreFix is required');
        break;
      case 'street':
        if (!coreMeta?.streetApiKey?.trim()) return toast.error('Street API Key is required');
        break;
      case 'agentpro':
        if (!coreMeta?.agentProApiKey?.trim()) return toast.error('Agent Pro API Key is required');
        if (!coreMeta?.agentProAgentId?.trim()) return toast.error('Agent Pro API Key is required');
        break;
      case 'rex':
        if (!coreMeta?.rexApiKey?.trim()) return toast.error('Rex API Key is required');
        break;
      case 'loop':
        if (!coreMeta?.loopApiKey?.trim()) return toast.error('Loop API Key is required');
        break;
      case 'mab':
        if (!coreMeta?.mabClientId?.trim()) return toast.error('MAB Client ID is required');
        if (!coreMeta?.mabClientSecret?.trim()) return toast.error('MAB Client Secret is required');
        if (!coreMeta?.mabIntroducers?.length)
          return toast.error('At least one MAB introducer is required');
        if (
          coreMeta.mabIntroducers.some(
            (i) => !i.firmId?.trim() || !i.branchId?.trim() || !i.staffId?.trim()
          )
        )
          return toast.error('Every MAB introducer needs a Firm ID, Branch ID and Staff ID');
        break;
      case 'mortgagekart':
        if (!coreMeta?.mortgageKartClientID?.trim())
          return toast.error('Mortgage Kart Client ID is required');
        if (!coreMeta?.mortgageKartClientSecret?.trim())
          return toast.error('Mortgage Kart Client Secret is required');
        break;
      case 'jupix':
        if (!coreMeta?.jupixApiKey?.trim()) return toast.error('Jupix API Key is required');
        if (!coreMeta?.jupixAgentCode?.trim())
          return toast.error('Jupix Agent Code is required');
        break;
      case 'custom':
        if (!coreMeta?.customKey?.trim()) return toast.error('Custom Key is required');
        break;
      case 'none':
        break;
      case 'hubspot':
        break;
      case 'everglades':
        // Key is per office (Offices tab), nothing to validate at the location level.
        break;
      default:
        return 'Key';
    }

    const { data, code } = await callUpdateCoreMeta(props.locationId as string, {
      integration: coreMeta.integration,
      passThru: coreMeta.passThru,
      qualification: coreMeta.qualification,
      homeFlowToken: coreMeta.homeFlowToken,
      leadProApiKey: coreMeta.leadProApiKey,
      reapitCustomerId: coreMeta.reapitCustomerId,
      acquaintApiKey: coreMeta.acquaintApiKey,
      acquaintSitePreFix: coreMeta.acquaintSitePreFix,
      altoAgentId: coreMeta.altoAgentId,
      altoAgentKey: coreMeta.altoAgentKey,
      altoAgentName: coreMeta.altoAgentName,
      altoAgentPassword: coreMeta.altoAgentPassword,
      altoAgentUsername: coreMeta.altoAgentUsername,
      streetApiKey: coreMeta.streetApiKey,
      agentProApiKey: coreMeta.agentProApiKey,
      agentProAgentId: coreMeta.agentProAgentId,
      customKey: coreMeta.customKey,
      offPatchValuationAddress: coreMeta.offPatchValuationAddress,
      website: coreMeta.website,
      supplierNumberEnabled: coreMeta.supplierNumberEnabled,
      ranApiKey: coreMeta.ranApiKey,
      rexApiKey: coreMeta.rexApiKey,
      loopApiKey: coreMeta.loopApiKey,
      mortgageKartClientID: coreMeta.mortgageKartClientID,
      mortgageKartClientSecret: coreMeta.mortgageKartClientSecret,
      jupixApiKey: coreMeta.jupixApiKey,
      jupixAgentCode: coreMeta.jupixAgentCode,
      zooplaProspectPlusEnabled: coreMeta.zooplaProspectPlusEnabled ?? false,
      zooplaClientId: coreMeta.zooplaClientId,
      zooplaClientSecret: coreMeta.zooplaClientSecret,
      zooplaFilterType: coreMeta.zooplaFilterType ?? 'group-id',
      zooplaFilterId: coreMeta.zooplaFilterId,
      wildcardAppointmentEmail: coreMeta.wildcardAppointmentEmail,
      wildcardReadEmail: coreMeta.wildcardReadEmail,
      mabClientId: coreMeta.mabClientId,
      mabClientSecret: coreMeta.mabClientSecret,
      mabIntroducers: coreMeta.mabIntroducers,
      salesServiceType: coreMeta.salesServiceType,
      lettingsServiceType: coreMeta.lettingsServiceType,
      valuationServiceType: coreMeta.valuationServiceType,
      businessHours: coreMeta.businessHours
    });
    if (data && code == 200) {
      toast.success('Updated Successfully');
    }
    setCoreMetaSave(false);
    setActionLoading(false);
  };

  if (props.locationId === undefined) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Alert message={'No location id found'} severity={'error'} />
      </Box>
    );
  }

  const coreLocation = coreData?.data?.data?.[0];
  const isMortgage = resolveBusinessType(coreLocation?.businessType) === 'mortgage';

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header Action Bar */}
      <Paper
        elevation={2}
        sx={{
          p: { xs: 1, sm: 2 },
          mb: 3,
          borderRadius: 2
        }}>
        <Stack
          direction="row"
          useFlexGap
          flexWrap="wrap"
          alignItems="center"
          justifyContent="space-between"
          sx={{ rowGap: 2, columnGap: 2 }}>
          {/* Controls group */}
          <Stack
            direction="row"
            useFlexGap
            flexWrap="wrap"
            alignItems="center"
            sx={{ rowGap: 1.5, columnGap: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Text sx={{ whiteSpace: 'nowrap' }}>Pass Thru</Text>
              <Select
                value={coreMeta?.passThru ?? 'off'}
                onChange={(e) => onSwitchChange('passThru', e.target.value)}
                size="small"
                sx={{ backgroundColor: '#ffffff', fontSize: 14, minWidth: 120 }}>
                <MenuItem value="off">Off</MenuItem>
                <MenuItem value="on">On</MenuItem>
                <MenuItem value="valuation">Valuation</MenuItem>
              </Select>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1}>
              <Text sx={{ whiteSpace: 'nowrap' }}>Qualification</Text>
              <Select
                value={coreMeta?.qualification ?? 'full-ai'}
                onChange={(e) => onSwitchChange('qualification', e.target.value)}
                size="small"
                sx={{ backgroundColor: '#ffffff', fontSize: 14, minWidth: 160 }}>
                <MenuItem value="full-ai">Full AI</MenuItem>
                <MenuItem value="business-hours">Business Hours</MenuItem>
                <MenuItem value="notification-only">Notification Only</MenuItem>
                <MenuItem value="processing-only">Processing Only</MenuItem>
              </Select>
            </Stack>
          </Stack>

          {/* Action buttons */}
          <Stack
            direction="row"
            useFlexGap
            flexWrap="wrap"
            alignItems="center"
            sx={{ rowGap: 1, columnGap: 1.5 }}>
            <Button primary={false} onClick={() => apiSyncUsers()}>
              <Text $fontColor="white">Sync Information With Core</Text>
            </Button>
            <Button primary={false} onClick={() => apiSyncContacts()} disabled={syncingContacts}>
              <Text $fontColor="white">
                {syncingContacts ? 'Syncing contacts…' : 'Sync contacts'}
              </Text>
            </Button>
            <Button variant="contained" onClick={() => setKnowledgeUpload(true)}>
              <Text>Upload Knowledge Base</Text>
            </Button>
            {coreMetaSave && (
              <Button primary={true} onClick={onSaveMeta}>
                <Text $fontColor="white">Save</Text>
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* Location Details & Valuation Section */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 3 }}>
        <Grid item xs={6}>
          <LocationDetails location={coreLocation} />
        </Grid>
        <Grid item xs={6}>
          <Paper
            elevation={2}
            sx={{
              p: { xs: 2, sm: 3 },
              backgroundColor: '#E8EDDF',
              borderRadius: 2,
              boxShadow:
                '0px 7px 140px rgba(141, 152, 121, 0.1), 0px 2px 32px rgba(141, 152, 121, 0.05), 0px 1px 10px rgba(141, 152, 121, 0.04)'
            }}>
            <Stack spacing={2}>
              {/* Website / Off‑patch (compact, moved here to avoid wasting space above) */}
              <Box>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  justifyContent="space-between"
                  spacing={1}
                  sx={{ mb: 1 }}>
                  <Text $fontSize={14} sx={{ fontWeight: 700 }}>
                    Website & Off‑patch
                  </Text>
                  <Text $fontSize={12} sx={{ opacity: 0.75 }}>
                    Quick metadata
                  </Text>
                </Stack>
                <Divider sx={{ mb: 1 }} />
                <Grid container spacing={{ xs: 1.5, md: 2 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Website"
                      labelSize={12}
                      labelWeight={600}
                      name="website"
                      fullWidth
                      value={coreMeta?.website ?? ''}
                      placeholder="example.com"
                      onChange={(e) => {
                        onSwitchChange('website', e.target.value);
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Off‑patch"
                      labelSize={12}
                      labelWeight={600}
                      name="offPatchValuationAddress"
                      fullWidth
                      value={coreMeta?.offPatchValuationAddress ?? ''}
                      placeholder="robot+offpatch@salesrook.com"
                      onChange={(e) => {
                        onSwitchChange('offPatchValuationAddress', e.target.value);
                      }}
                    />
                  </Grid>
                </Grid>
                {isMortgage && (
                  <Grid container spacing={{ xs: 1.5, md: 2 }} sx={{ mt: 0 }}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Wildcard Appointment Email"
                        labelSize={12}
                        labelWeight={600}
                        name="wildcardAppointmentEmail"
                        fullWidth
                        value={coreMeta?.wildcardAppointmentEmail ?? ''}
                        onChange={(e) => onSwitchChange('wildcardAppointmentEmail', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Wildcard Read Email"
                        labelSize={12}
                        labelWeight={600}
                        name="wildcardReadEmail"
                        fullWidth
                        value={coreMeta?.wildcardReadEmail ?? ''}
                        onChange={(e) => onSwitchChange('wildcardReadEmail', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                )}
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Cards & Integration Section */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 3 }} alignItems="stretch">
        <Grid item xs={12} md={6}>
          <Paper
            elevation={2}
            sx={{
              p: { xs: 2, sm: 3 },
              height: '100%',
              backgroundColor: '#E8EDDF',
              borderRadius: 2,
              boxShadow:
                '0px 7px 140px rgba(141, 152, 121, 0.1), 0px 2px 32px rgba(141, 152, 121, 0.05), 0px 1px 10px rgba(141, 152, 121, 0.04)'
            }}>
            <Stack spacing={2}>
              <Box>
                <Text mb={1} $fontSize={16} sx={{ fontWeight: 600 }}>
                  Integration Settings
                </Text>
                <Divider sx={{ mb: 2 }} />
                <Select
                  value={coreMeta?.integration ?? 'none'}
                  onChange={(e) =>
                    onSwitchChange('integration', e.target.value as CoreIntegrationType)
                  }
                  fullWidth
                  size="small"
                  sx={{ backgroundColor: '#ffffff', fontSize: 14 }}>
                  <MenuItem value="none">None</MenuItem>
                  <MenuItem value="reapit">Reapit</MenuItem>
                  <MenuItem value="leadpro">LeadPro</MenuItem>
                  <MenuItem value="homeflow">HomeFlow</MenuItem>
                  <MenuItem value="alto">Alto</MenuItem>
                  <MenuItem value="acquaint">Acquaint</MenuItem>
                  <MenuItem value="street">Street</MenuItem>
                  <MenuItem value="agentpro">Agent Pro</MenuItem>
                  <MenuItem value="rex">REX CRM</MenuItem>
                  <MenuItem value="loop">Loop</MenuItem>
                  <MenuItem value="mab">MAB</MenuItem>
                  <MenuItem value="mortgagekart">Mortgage Kart</MenuItem>
                  <MenuItem value="everglades">Everglades</MenuItem>
                  <MenuItem value="jupix">Jupix</MenuItem>
                  <MenuItem value="hubspot">HubSpot</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </Box>

              {/* Reapit Integration */}
              {coreMeta?.integration === 'reapit' && (
                <TextField
                  label="Reapit Customer ID"
                  name="reapitCustomerId"
                  fullWidth
                  value={coreMeta?.reapitCustomerId ?? ''}
                  onChange={(e) => onSwitchChange('reapitCustomerId', e.target.value)}
                />
              )}

              {/* HomeFlow Integration */}
              {coreMeta?.integration === 'homeflow' && (
                <TextField
                  label="Home Flow Token"
                  name="homeFlowToken"
                  fullWidth
                  value={coreMeta?.homeFlowToken ?? ''}
                  onChange={(e) => onSwitchChange('homeFlowToken', e.target.value)}
                />
              )}

              {/* LeadPro Integration */}
              {coreMeta?.integration === 'leadpro' && (
                <TextField
                  label="LeadPro API Key"
                  name="leadProApiKey"
                  fullWidth
                  value={coreMeta?.leadProApiKey ?? ''}
                  onChange={(e) => onSwitchChange('leadProApiKey', e.target.value)}
                />
              )}

              {/* Acquaint Integration */}
              {coreMeta?.integration === 'acquaint' && (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Acquaint API Key"
                      name="acquaintApiKey"
                      fullWidth
                      value={coreMeta?.acquaintApiKey ?? ''}
                      onChange={(e) => onSwitchChange('acquaintApiKey', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Acquaint Site PreFix"
                      name="acquaintSitePreFix"
                      fullWidth
                      value={coreMeta?.acquaintSitePreFix ?? ''}
                      onChange={(e) => onSwitchChange('acquaintSitePreFix', e.target.value)}
                    />
                  </Grid>
                </Grid>
              )}

              {/* Street Integration */}
              {coreMeta?.integration === 'street' && (
                <TextField
                  label="Street API Key"
                  name="streetApiKey"
                  fullWidth
                  value={coreMeta?.streetApiKey ?? ''}
                  onChange={(e) => onSwitchChange('streetApiKey', e.target.value)}
                />
              )}

              {/* Agent Pro Integration */}
              {coreMeta?.integration === 'agentpro' && (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Agent Pro API Key"
                      name="agentProApiKey"
                      fullWidth
                      value={coreMeta?.agentProApiKey ?? ''}
                      onChange={(e) => onSwitchChange('agentProApiKey', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Agent Pro Agent ID"
                      name="agentProAgentId"
                      fullWidth
                      value={coreMeta?.agentProAgentId ?? ''}
                      onChange={(e) => onSwitchChange('agentProAgentId', e.target.value)}
                    />
                  </Grid>
                </Grid>
              )}

              {/* Custom Integration */}
              {coreMeta?.integration === 'custom' && (
                <TextField
                  label="Custom Key"
                  name="customKey"
                  fullWidth
                  value={coreMeta?.customKey ?? ''}
                  onChange={(e) => onSwitchChange('customKey', e.target.value)}
                />
              )}

              {/* Rex Integration */}
              {coreMeta?.integration === 'rex' && (
                <TextField
                  label="Rex API Key"
                  name="rexApiKey"
                  fullWidth
                  value={coreMeta?.rexApiKey ?? ''}
                  onChange={(e) => onSwitchChange('rexApiKey', e.target.value)}
                />
              )}

              {/* Loop Integration */}
              {coreMeta?.integration === 'loop' && (
                <TextField
                  label="Loop API Key"
                  name="loopApiKey"
                  fullWidth
                  value={coreMeta?.loopApiKey ?? ''}
                  onChange={(e) => onSwitchChange('loopApiKey', e.target.value)}
                />
              )}

              {/* MAB Integration */}
              {coreMeta?.integration === 'mab' && (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="MAB Client ID"
                      name="mabClientId"
                      fullWidth
                      value={coreMeta?.mabClientId ?? ''}
                      onChange={(e) => onSwitchChange('mabClientId', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="MAB Client Secret"
                      name="mabClientSecret"
                      fullWidth
                      value={coreMeta?.mabClientSecret ?? ''}
                      onChange={(e) => onSwitchChange('mabClientSecret', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Stack spacing={1.5}>
                      <Text $fontSize={14} sx={{ fontWeight: 700 }}>
                        Introducers
                      </Text>
                      {(coreMeta?.mabIntroducers ?? []).map((introducer, index) => (
                        <Stack
                          key={index}
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={2}
                          alignItems={{ xs: 'stretch', sm: 'flex-end' }}
                          sx={{ width: '100%' }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <TextField
                              label="Firm ID"
                              name={`mabIntroducerFirmId-${index}`}
                              fullWidth
                              value={introducer.firmId ?? ''}
                              onChange={(e) =>
                                onIntroducersChange(
                                  (coreMeta?.mabIntroducers ?? []).map((item, i) =>
                                    i === index ? { ...item, firmId: e.target.value } : item
                                  )
                                )
                              }
                            />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <TextField
                              label="Branch ID"
                              name={`mabIntroducerBranchId-${index}`}
                              fullWidth
                              value={introducer.branchId ?? ''}
                              onChange={(e) =>
                                onIntroducersChange(
                                  (coreMeta?.mabIntroducers ?? []).map((item, i) =>
                                    i === index ? { ...item, branchId: e.target.value } : item
                                  )
                                )
                              }
                            />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <TextField
                              label="Staff ID"
                              name={`mabIntroducerStaffId-${index}`}
                              fullWidth
                              value={introducer.staffId ?? ''}
                              onChange={(e) =>
                                onIntroducersChange(
                                  (coreMeta?.mabIntroducers ?? []).map((item, i) =>
                                    i === index ? { ...item, staffId: e.target.value } : item
                                  )
                                )
                              }
                            />
                          </Box>
                          <Box sx={{ flexShrink: 0 }}>
                            <Button
                              primary={false}
                              onClick={() =>
                                onIntroducersChange(
                                  (coreMeta?.mabIntroducers ?? []).filter((_, i) => i !== index)
                                )
                              }>
                              <Text $fontColor="white">Remove</Text>
                            </Button>
                          </Box>
                        </Stack>
                      ))}
                      <Box>
                        <Button
                          primary={true}
                          onClick={() =>
                            onIntroducersChange([
                              ...(coreMeta?.mabIntroducers ?? []),
                              { firmId: '', branchId: '', staffId: '' }
                            ])
                          }>
                          <Text $fontColor="white">Add Introducer</Text>
                        </Button>
                      </Box>
                    </Stack>
                  </Grid>
                </Grid>
              )}

              {/* Mortgage Kart Integration */}
              {coreMeta?.integration === 'mortgagekart' && (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Mortgage Kart Client ID"
                      name="mortgageKartClientID"
                      fullWidth
                      value={coreMeta?.mortgageKartClientID ?? ''}
                      onChange={(e) => onSwitchChange('mortgageKartClientID', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Mortgage Kart Client Secret"
                      name="mortgageKartClientSecret"
                      fullWidth
                      value={coreMeta?.mortgageKartClientSecret ?? ''}
                      onChange={(e) => onSwitchChange('mortgageKartClientSecret', e.target.value)}
                    />
                  </Grid>
                </Grid>
              )}

              {/* Jupix Integration */}
              {coreMeta?.integration === 'jupix' && (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Jupix API Key"
                      name="jupixApiKey"
                      fullWidth
                      value={coreMeta?.jupixApiKey ?? ''}
                      onChange={(e) => onSwitchChange('jupixApiKey', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Jupix Agent Code"
                      name="jupixAgentCode"
                      fullWidth
                      value={coreMeta?.jupixAgentCode ?? ''}
                      onChange={(e) => onSwitchChange('jupixAgentCode', e.target.value)}
                    />
                  </Grid>
                </Grid>
              )}

              {/* Alto Integration */}
              {coreMeta?.integration === 'alto' && (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Agent Key"
                      name="altoAgentKey"
                      fullWidth
                      value={coreMeta?.altoAgentKey ?? ''}
                      onChange={(e) => onSwitchChange('altoAgentKey', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Agent ID"
                      name="altoAgentId"
                      fullWidth
                      value={coreMeta?.altoAgentId ?? ''}
                      onChange={(e) => onSwitchChange('altoAgentId', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Agent Name"
                      name="altoAgentName"
                      fullWidth
                      value={coreMeta?.altoAgentName ?? ''}
                      onChange={(e) => onSwitchChange('altoAgentName', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Agent Username"
                      name="altoAgentUsername"
                      fullWidth
                      value={coreMeta?.altoAgentUsername ?? ''}
                      onChange={(e) => onSwitchChange('altoAgentUsername', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Agent Password"
                      name="altoAgentPassword"
                      fullWidth
                      value={coreMeta?.altoAgentPassword ?? ''}
                      onChange={(e) => onSwitchChange('altoAgentPassword', e.target.value)}
                    />
                  </Grid>
                </Grid>
              )}

              {/* Supplier Number Toggle */}
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <Text $fontSize={14} sx={{ fontWeight: 600 }}>
                    Referral Network
                  </Text>
                  <Select
                    value={coreMeta?.supplierNumberEnabled ?? 'off'}
                    onChange={(e) => onSwitchChange('supplierNumberEnabled', e.target.value)}
                    size="small"
                    sx={{
                      backgroundColor: '#ffffff',
                      fontSize: 14,
                      minWidth: 100
                    }}>
                    <MenuItem value="off">Off</MenuItem>
                    <MenuItem value="ran">RAN</MenuItem>
                  </Select>
                </Stack>
                {coreMeta?.supplierNumberEnabled === 'ran' && (
                  <TextField
                    label="X-API-KEY"
                    name="ranApiKey"
                    fullWidth
                    value={coreMeta?.ranApiKey ?? ''}
                    onChange={(e) => onSwitchChange('ranApiKey', e.target.value)}
                  />
                )}
              </Box>

              {/* Zoopla Prospect Plus */}
              <Box>
                <Text mb={1} $fontSize={16} sx={{ fontWeight: 600 }}>
                  Zoopla Prospect Plus
                </Text>
                <Divider sx={{ mb: 1 }} />
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={coreMeta?.zooplaProspectPlusEnabled ?? false}
                      onChange={(e) =>
                        onSwitchChange('zooplaProspectPlusEnabled', e.target.checked)
                      }
                    />
                  }
                  label={<Text $fontSize={14}>Enable Zoopla Prospect Plus</Text>}
                />
                {coreMeta?.zooplaProspectPlusEnabled && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Zoopla Client ID"
                        name="zooplaClientId"
                        fullWidth
                        value={coreMeta?.zooplaClientId ?? ''}
                        onChange={(e) => onSwitchChange('zooplaClientId', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Zoopla Client Secret"
                        name="zooplaClientSecret"
                        fullWidth
                        type={showZooplaSecret ? 'text' : 'password'}
                        value={coreMeta?.zooplaClientSecret ?? ''}
                        onChange={(e) => onSwitchChange('zooplaClientSecret', e.target.value)}
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setShowZooplaSecret((v) => !v)}
                              aria-label={showZooplaSecret ? 'Hide secret' : 'Show secret'}>
                              {showZooplaSecret ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Text $fontSize={14} sx={{ mt: 1.5, mb: 1 }}>
                        Filter Type
                      </Text>
                      <Select
                        value={coreMeta?.zooplaFilterType ?? 'group-id'}
                        onChange={(e) => onSwitchChange('zooplaFilterType', e.target.value)}
                        fullWidth
                        size="small"
                        sx={{ backgroundColor: '#ffffff', fontSize: 14 }}>
                        <MenuItem value="group-id">Group ID</MenuItem>
                        <MenuItem value="company-id">Company ID</MenuItem>
                        <MenuItem value="branch-id">Branch ID</MenuItem>
                      </Select>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Filter ID"
                        name="zooplaFilterId"
                        fullWidth
                        value={coreMeta?.zooplaFilterId ?? ''}
                        onChange={(e) => onSwitchChange('zooplaFilterId', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                )}
              </Box>
            </Stack>
          </Paper>
        </Grid>

        {/* Service Settings */}
        <Grid item xs={12} md={6}>
          <Paper
            elevation={2}
            sx={{
              p: { xs: 2, sm: 3 },
              height: '100%',
              backgroundColor: '#E8EDDF',
              borderRadius: 2,
              boxShadow:
                '0px 7px 140px rgba(141, 152, 121, 0.1), 0px 2px 32px rgba(141, 152, 121, 0.05), 0px 1px 10px rgba(141, 152, 121, 0.04)'
            }}>
            <Stack spacing={2}>
              <Box>
                <Text mb={1} $fontSize={16} sx={{ fontWeight: 600 }}>
                  Service Settings
                </Text>
                <Divider sx={{ mb: 2 }} />
                <Text $fontSize={12} sx={{ fontWeight: 700, mb: 1, opacity: 0.8 }}>
                  Service Type
                </Text>
                <Grid container spacing={2}>
                  {(
                    [
                      { key: 'salesServiceType', label: 'Sales' },
                      { key: 'lettingsServiceType', label: 'Lettings' },
                      { key: 'valuationServiceType', label: 'Valuation' }
                    ] as { key: keyof CoreMeta; label: string }[]
                  ).map((svc) => (
                    <Grid item xs={12} sm={4} key={svc.key}>
                      <Text $fontSize={12} sx={{ fontWeight: 600, mb: 0.5 }}>
                        {svc.label}
                      </Text>
                      <Select
                        value={(coreMeta?.[svc.key] as CoreServiceType) ?? 'qualify'}
                        onChange={(e) => onSwitchChange(svc.key, e.target.value)}
                        fullWidth
                        size="small"
                        sx={{ backgroundColor: '#ffffff', fontSize: 14 }}>
                        {SERVICE_TYPE_OPTIONS.map((o) => (
                          <MenuItem key={o.value} value={o.value}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <Box>
                <Text $fontSize={14} sx={{ fontWeight: 700, mb: 1 }}>
                  Business Hours
                </Text>
                <Divider sx={{ mb: 1.5 }} />
                <Stack spacing={1}>
                  {BUSINESS_DAYS.map(({ key, label }) => {
                    const hours = coreMeta?.businessHours?.[key] ?? DEFAULT_BUSINESS_HOURS[key];
                    return (
                      <Stack
                        key={key}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        useFlexGap
                        flexWrap="wrap">
                        <FormControlLabel
                          sx={{ minWidth: 130, m: 0 }}
                          control={
                            <Switch
                              size="small"
                              checked={hours.enabled}
                              onChange={(e) =>
                                onBusinessHoursChange(key, 'enabled', e.target.checked)
                              }
                            />
                          }
                          label={<Text $fontSize={13}>{label}</Text>}
                        />
                        <Select
                          value={hours.open}
                          disabled={!hours.enabled}
                          onChange={(e) => onBusinessHoursChange(key, 'open', e.target.value)}
                          size="small"
                          sx={{ backgroundColor: '#ffffff', fontSize: 13, minWidth: 110 }}>
                          {TIME_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              {o.label}
                            </MenuItem>
                          ))}
                        </Select>
                        <Text $fontSize={13}>to</Text>
                        <Select
                          value={hours.close}
                          disabled={!hours.enabled}
                          onChange={(e) => onBusinessHoursChange(key, 'close', e.target.value)}
                          size="small"
                          sx={{ backgroundColor: '#ffffff', fontSize: 13, minWidth: 110 }}>
                          {TIME_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>
                              {o.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </Stack>
                    );
                  })}
                </Stack>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Core Data Sections (Tabbed) */}
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 2
        }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={1}
          sx={{ mb: 1.5 }}>
          <Box>
            <Text $fontSize={16} sx={{ fontWeight: 800 }}>
              Core Data
            </Text>
            <Text $fontSize={12} sx={{ opacity: 0.8 }}>
              Explore Offices, Users, and Contacts for this location
            </Text>
          </Box>
        </Stack>

        <Tabs
          value={activeCoreTab}
          onChange={async (_e, v: CoreDataTabKey) => {
            setActiveCoreTab(v);
            await setCoreTabInUrl(v);
          }}
          variant="scrollable"
          allowScrollButtonsMobile
          TabIndicatorProps={{ style: { display: 'none' } }}
          sx={{
            mb: 1,
            minHeight: 44,
            '& .MuiTabs-flexContainer': { gap: 1, paddingBottom: 4 },
            '& .MuiTab-root': {
              textTransform: 'none',
              minHeight: 40,
              px: 1.5,
              borderRadius: 999,
              border: '1px solid rgba(141, 152, 121, 0.35)',
              backgroundColor: 'rgba(255, 255, 255, 0.55)',
              fontWeight: 700
            },
            '& .MuiTab-root.Mui-selected': {
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              borderColor: 'rgba(141, 152, 121, 0.85)',
              boxShadow: '0px 6px 18px rgba(141, 152, 121, 0.15)'
            }
          }}>
          <Tab
            value="offices"
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>Offices</span>
              </Stack>
            }
          />
          <Tab
            value="users"
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>Users</span>
                <Chip
                  size="small"
                  label={coreLocation?.totalUsers ?? 0}
                  sx={{
                    borderRadius: 999,
                    fontWeight: 800,
                    backgroundColor: 'rgba(141, 152, 121, 0.22)'
                  }}
                />
              </Stack>
            }
          />
          <Tab
            value="contacts"
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>Contacts</span>
                <Chip
                  size="small"
                  label={coreLocation?.totalContacts ?? 0}
                  sx={{
                    borderRadius: 999,
                    fontWeight: 800,
                    backgroundColor: 'rgba(141, 152, 121, 0.22)'
                  }}
                />
              </Stack>
            }
          />
        </Tabs>
        <Box>
          {activeCoreTab === 'offices' && (
            <CoreOffices
              locationId={props.locationId}
              businessType={coreLocation?.businessType}
              integration={coreMeta?.integration}
            />
          )}
          {activeCoreTab === 'users' && <CoreUsers locationId={props.locationId} />}
          {activeCoreTab === 'contacts' && (
            <CoreContacts locationId={props.locationId!} refreshTrigger={contactsRefreshTrigger} />
          )}
        </Box>
      </Paper>

      {/* Upload Knowledge Base Modal */}
      {knowledgeUpload && (
        <UploadKnowledgeBaseCSV
          locationId={props.locationId}
          open={knowledgeUpload}
          onClose={() => setKnowledgeUpload(false)}
          onSuccess={() => setKnowledgeUpload(false)}
        />
      )}
    </Box>
  );
};

export default CoreDetails;
